import { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { toRpcFilters } from '@/lib/filters/rpc';
import { isUniqueViolation } from '@/lib/error-handling';
import { getInvalidationKeys } from '@/lib/query-keys';
import type { Contact } from '@/types/contact';
import type { UseBulkSelectionReturn } from '@/hooks/useBulkSelection';

const groupSchema = z.object({
  name: z.string().min(1, 'Group name is required'),
});

interface Group {
  id: string;
  name: string;
  contact_count: number;
}

interface AddToGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedContacts: Contact[];
  bulkSelection?: UseBulkSelectionReturn;
  onSuccess?: () => void;
}

export function AddToGroupDialog({
  open,
  onOpenChange,
  selectedContacts,
  bulkSelection,
  onSuccess,
}: AddToGroupDialogProps) {
  const effectiveCount = bulkSelection?.effectiveCount ?? selectedContacts.length;
  const isAllMatchingMode = bulkSelection?.mode === 'all_matching';
  const [isLoading, setIsLoading] = useState(false);
  const [existingGroups, setExistingGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'new' | 'existing'>('new');
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaces();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof groupSchema>>({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      name: '',
    },
  });

  // Resolve contact IDs for all_matching mode
  const resolveContactIds = async (): Promise<string[]> => {
    if (!isAllMatchingMode || !currentWorkspace || !bulkSelection?.filterState) {
      return selectedContacts.map(c => c.id);
    }

    // Use .range() to bypass PostgREST's default 1000-row limit
    const { data: matchedIds, error } = await supabase
      .rpc('get_filtered_contact_ids', {
        p_workspace_id: currentWorkspace.id,
        p_filters: bulkSelection.filterState ? toRpcFilters(bulkSelection.filterState) : null,
      })
      .range(0, 99999);

    if (error || !matchedIds?.length) {
      throw new Error(error?.message || 'Failed to resolve contacts');
    }

    return matchedIds
      .map((row: { contact_id: string }) => row.contact_id)
      .filter((id: string) => !bulkSelection.excludedIds.has(id));
  };

  useEffect(() => {
    if (open) {
      fetchGroups();
    } else {
      form.reset({ name: '' });
      setSelectedGroup('');
      setActiveTab('new');
    }
  }, [open, form]);

  const fetchGroups = async () => {
    if (!currentWorkspace) return;

    try {
      const { data, error } = await supabase
        .from('view_group_counts')
        .select('group_id, group_name, contact_count')
        .eq('workspace_id', currentWorkspace.id);

      if (error) throw error;

      setExistingGroups(data.map(group => ({
        id: group.group_id,
        name: group.group_name,
        contact_count: group.contact_count
      })));
    } catch (error) {
      console.error('Error fetching groups:', error);
      captureError(error as Error, { feature: 'contacts-add-to-group-dialog-fetch-groups' });
      toast({
        title: 'Error',
        description: 'Failed to load existing groups',
        variant: 'destructive',
      });
    }
  };

  const addToExistingGroup = async () => {
    if (!selectedGroup || !currentWorkspace) {
      toast({
        title: 'Error',
        description: 'Please select a group',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const contactIds = await resolveContactIds();

      if (contactIds.length === 0) {
        toast({
          title: 'Error',
          description: 'No contacts to add',
          variant: 'destructive',
        });
        return;
      }

      const { data, error } = await supabase.rpc('bulk_add_to_group', {
        p_contact_ids: contactIds,
        p_workspace_id: currentWorkspace.id,
        p_group_id: selectedGroup,
      });

      if (error) throw error;

      const addedCount = data?.added_count ?? contactIds.length;
      toast({
        description: `Added ${addedCount} contact${addedCount !== 1 ? 's' : ''} to group`,
      });

      // Invalidate groups queries to refresh nav and dropdowns
      const invalidationKeys = getInvalidationKeys.afterGroupMutation(currentWorkspace.id);
      await Promise.all(
        invalidationKeys.map(key => queryClient.invalidateQueries({ queryKey: key }))
      );

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error adding to group:', error);
      captureError(error as Error, { feature: 'bulk-add-to-group' });
      toast({
        title: 'Error',
        description: 'Failed to add contacts to group. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createNewGroup = async (values: z.infer<typeof groupSchema>) => {
    if (!user || !currentWorkspace) return;

    setIsLoading(true);
    try {
      // Create the group
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .insert({
          group_name: values.name.trim().toLowerCase(),
          workspace_id: currentWorkspace.id,
          created_by: user.id,
        })
        .select()
        .single();

      if (groupError) throw groupError;

      // Resolve and add contacts
      const contactIds = await resolveContactIds();

      if (contactIds.length > 0) {
        const { data, error } = await supabase.rpc('bulk_add_to_group', {
          p_contact_ids: contactIds,
          p_workspace_id: currentWorkspace.id,
          p_group_id: group.id,
        });

        if (error) throw error;

        const addedCount = data?.added_count ?? contactIds.length;
        toast({
          description: `Created group "${values.name}" with ${addedCount} contact${addedCount !== 1 ? 's' : ''}`,
        });
      } else {
        toast({
          description: `Created group "${values.name}"`,
        });
      }

      // Invalidate groups queries to refresh nav and dropdowns
      const invalidationKeys = getInvalidationKeys.afterGroupMutation(currentWorkspace.id);
      await Promise.all(
        invalidationKeys.map(key => queryClient.invalidateQueries({ queryKey: key }))
      );

      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error creating group:', error);
      if (isUniqueViolation(error)) {
        toast({
          title: 'Group already exists',
          description: `A group named "${values.name.trim()}" already exists in this workspace.`,
          variant: 'destructive',
        });
        return;
      }
      captureError(error as Error, { feature: 'create-group' });
      toast({
        title: 'Error',
        description: 'Failed to create group. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add to Group</DialogTitle>
          <DialogDescription>
            Add {effectiveCount.toLocaleString()} selected contact{effectiveCount !== 1 ? 's' : ''} to a group
            {isAllMatchingMode && (
              <span className="block text-xs text-muted-foreground mt-1">
                (All matching results selected)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'new' | 'existing')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="new">New Group</TabsTrigger>
            <TabsTrigger value="existing">Existing Group</TabsTrigger>
          </TabsList>

          <TabsContent value="new">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(createNewGroup)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Group Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter group name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Create & Add'}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="existing">
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="text-sm font-medium">Select Group</div>
                <Select
                  value={selectedGroup}
                  onValueChange={setSelectedGroup}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a group" />
                  </SelectTrigger>
                  <SelectContent>
                    {existingGroups.length === 0 ? (
                      <SelectItem value="_none" disabled>
                        No groups available
                      </SelectItem>
                    ) : (
                      existingGroups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name} ({group.contact_count} contacts)
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button
                  onClick={addToExistingGroup}
                  disabled={isLoading || !selectedGroup}
                >
                  {isLoading ? 'Adding...' : 'Add to Group'}
                </Button>
              </DialogFooter>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
